import { ClientRect, GetPositionAndMaxHeight } from './modalDropdownTypes';
import { defaultPosition } from '../dropdownConsts';
import {
  ReactNode,
  RefObject,
  PropsWithChildren,
  useRef,
  useState,
  useCallback,
} from 'react';
import styled from 'styled-components';
import {
  DropdownMenuBodyWrapper,
  DropdownMenuHeaderWrapper,
  DropdownMenuWrapper,
  DropdownSize,
} from '@ardoq/dropdown-menu-ui';
import { useEffectOnce, useIsMounted, useResizeObserver } from '@ardoq/hooks';
import { usePreventScroll } from '@react-aria/overlays';
import { isEqual } from 'lodash';
import { zIndex, colors } from '@ardoq/design-tokens';

export interface ModalBoxProps {
  getHostBox: () => ClientRect;
  getPositionAndMaxHeight: GetPositionAndMaxHeight;
  headerContent?: ReactNode;
  footerContent?: ReactNode;
  isScrollable?: boolean;
  dropdownSize?: DropdownSize;
  minDropdownSize?: DropdownSize;
  maxDropdownSize?: DropdownSize;
  isScrollLockDisabled?: boolean;
  skipAnimation?: boolean;
  clickNamespace?: string;
  testId?: string;
  role?: string;
  'aria-label'?: string;
  noScrollOnWrapper?: boolean;
  useDisplayFlexForMainContent?: boolean;
  setContentWidthToHostWidth?: boolean;
}

export const ModalBoxDiv = styled(DropdownMenuWrapper)`
  background-color: ${colors.surfaceDefault};
  left: -1000px;
  position: fixed;
  top: -1000px;
  user-select: none;
  z-index: ${zIndex.MODAL_BACKDROP + 2};
`;

const useModalBoxStyle = (
  props: ModalBoxProps,
  contentRef: RefObject<null | HTMLElement>
) => {
  const [style, setStyle] = useState<React.CSSProperties | undefined>();
  const calculateBoxStyle = useCallback(() => {
    const hostBox = props.getHostBox();

    const modalBox = contentRef.current
      ? contentRef.current.getBoundingClientRect()
      : defaultPosition;

    const {
      isScrollable = true,
      getPositionAndMaxHeight,
      setContentWidthToHostWidth,
    } = props;
    const { left, top, maxHeight, maxWidth, bottom, refBoxWidth } =
      getPositionAndMaxHeight(hostBox, modalBox, isScrollable);

    const newStyle = {
      top: bottom ? 'unset' : `${top}px`,
      bottom: bottom ? `${bottom}px` : 'unset',
      left: `${left}px`,
      maxHeight: `${maxHeight}px`,
      maxWidth: maxWidth ? `${maxWidth}px` : undefined,
      overflow: isScrollable ? 'auto' : undefined,
      width: setContentWidthToHostWidth ? `${refBoxWidth}px` : undefined,
    };

    if (!isEqual(style, newStyle)) {
      setStyle(newStyle);
    }
  }, [contentRef, props, style]);
  useResizeObserver(contentRef, calculateBoxStyle);
  useEffectOnce(calculateBoxStyle);
  return style;
};

export const ModalBox = ({
  skipAnimation,
  clickNamespace,
  testId,
  headerContent,
  footerContent,
  noScrollOnWrapper = false,
  useDisplayFlexForMainContent = false,
  ...props
}: PropsWithChildren<ModalBoxProps>) => {
  const contentRef = useRef(null);
  const style = useModalBoxStyle(props, contentRef);
  usePreventScroll({ isDisabled: props.isScrollLockDisabled });
  const isVisible = useIsMounted();
  return (
    <ModalBoxDiv
      $dropdownSize={props.dropdownSize || DropdownSize.AUTO}
      $minDropdownSize={props.minDropdownSize}
      $maxDropdownSize={props.maxDropdownSize}
      style={style || {}}
      ref={contentRef}
      $isVisible={skipAnimation || isVisible || false}
      data-click-namespace={clickNamespace}
      data-test-id={testId}
      role={props.role}
      aria-label={props['aria-label']}
    >
      {headerContent && (
        <DropdownMenuHeaderWrapper>{headerContent}</DropdownMenuHeaderWrapper>
      )}
      <DropdownMenuBodyWrapper
        $noScrollOnWrapper={noScrollOnWrapper}
        $useDisplayFlexForMainContent={useDisplayFlexForMainContent}
      >
        {props.children}
      </DropdownMenuBodyWrapper>
      {footerContent && (
        <DropdownMenuHeaderWrapper>{footerContent}</DropdownMenuHeaderWrapper>
      )}
    </ModalBoxDiv>
  );
};
